import { ReactNode } from 'react';
import { Building2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SupplierWithStatus } from './types';
import { SupplierCard } from './SupplierCard';

interface SupplierGridProps {
  suppliers: SupplierWithStatus[];
  filteredSuppliers: SupplierWithStatus[];
  paginatedSuppliers: SupplierWithStatus[];
  currentTab: 'all' | 'sites' | 'groups';
  onTabChange: (tab: 'all' | 'sites' | 'groups') => void;
  onNavigate: (path: string) => void;
  onSelectGroup?: (groupName: string) => void;
  searchFilterBar?: ReactNode;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function SupplierGrid({
  suppliers: _suppliers,
  filteredSuppliers,
  paginatedSuppliers,
  currentTab,
  onTabChange,
  onNavigate,
  onSelectGroup: _onSelectGroup,
  searchFilterBar,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: SupplierGridProps) {
  const startItem = filteredSuppliers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, filteredSuppliers.length);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <>
      {/* Suppliers Header with Tabs and Search */}
      <div className="space-y-4 border-b pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Suppliers</h2>
          <div className="flex items-center gap-3">
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className="w-[80px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
              </SelectContent>
            </Select>
            <Tabs value={currentTab} onValueChange={(v) => onTabChange(v as typeof currentTab)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="sites">Supplier Companies</TabsTrigger>
                <TabsTrigger value="groups">Supplier Groups</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        {searchFilterBar && searchFilterBar}
      </div>

      {/* Results count + Top Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {startItem}–{endItem} of {filteredSuppliers.length} {filteredSuppliers.length === 1 ? 'supplier' : 'suppliers'}.
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map((p) => (
              <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm" onClick={() => onPageChange(p)} className="min-w-[32px]">
                {p}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Supplier Cards Grid */}
      {paginatedSuppliers.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No matching records found</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-tour="supplier-cards">
          {paginatedSuppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-4">
          <Button variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {getPageNumbers().map((p) => (
            <Button
              key={p}
              variant={p === currentPage ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(p)}
              className="min-w-[32px]"
            >
              {p}
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </>
  );
}
